import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, BookOpen, ArrowRight, PlayCircle, FileText, CheckCircle } from 'lucide-react';
import { topicsApi, Topic } from '../api/topics';
import { useProgressStore } from '../state/progressStore';
import { useModuleCompletion } from '../hooks/useModuleCompletion';

// Image mapping for modules based on their content
const getModuleImage = (heading: string, index: number): string => {
  const headingLower = heading.toLowerCase();
  
  // Specific module mappings for better image matching
  if (headingLower.includes('toepassingen en impact') || headingLower.includes('applications and impact')) {
    return 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&h=250&fit=crop&crop=center'; // Laptop interaction
  }
  else if (headingLower.includes('impact op verschillende sectoren') || headingLower.includes('impact on different sectors')) {
    return 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=250&fit=crop&crop=center'; // Data visualization
  }
  else if (headingLower.includes('kritische blik en uitdagingen') || headingLower.includes('critical perspective')) {
    return 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400&h=250&fit=crop&crop=center'; // AI/Neural network
  }
  else if (headingLower.includes('ethiek, regulering en toekomst') || headingLower.includes('ethics, regulation')) {
    return 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=400&h=250&fit=crop&crop=center'; // Business/collaboration
  }
  else if (headingLower.includes('strategische vooruitblik') || headingLower.includes('strategic outlook')) {
    return 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=250&fit=crop&crop=center'; // Innovation/future
  }
  // GPT models - Neural network/AI image
  else if (headingLower.includes('gpt') || headingLower.includes('revolutie') || headingLower.includes('model') || headingLower.includes('chatgpt')) {
    return 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400&h=250&fit=crop&crop=center';
  } 
  // Competition - Business/competition image
  else if (headingLower.includes('concurrentie') || headingLower.includes('competition') || headingLower.includes('meta') || headingLower.includes('google') || headingLower.includes('anthropic')) {
    return 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=400&h=250&fit=crop&crop=center';
  } 
  // Prompt Engineering - Coding/programming image
  else if (headingLower.includes('prompt') || headingLower.includes('engineering') || headingLower.includes('best practices') || headingLower.includes('richtlijnen') || headingLower.includes('technieken')) {
    return 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=250&fit=crop&crop=center';
  } 
  // Data & AI - Data visualization image
  else if (headingLower.includes('data') || headingLower.includes('analyse') || headingLower.includes('infrastructuur') || headingLower.includes('governance')) {
    return 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=250&fit=crop&crop=center';
  }
  // Applications & Future - Innovation/future image
  else if (headingLower.includes('toepassingen') || headingLower.includes('toekomst') || headingLower.includes('applications') || headingLower.includes('future') || headingLower.includes('vaardigheden')) {
    return 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=250&fit=crop&crop=center';
  }
  // AI Fundamentals - Learning/education image
  else if (headingLower.includes('fundamentals') || headingLower.includes('intro') || headingLower.includes('inleiding') || headingLower.includes('definitie') || headingLower.includes('kernbegrippen') || headingLower.includes('historische') || index === 0) {
    return 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&h=250&fit=crop&crop=center';
  }
  // Risk and limitations
  else if (headingLower.includes('beperkingen') || headingLower.includes('risicos') || headingLower.includes('limitations') || headingLower.includes('risks')) {
    return 'https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=400&h=250&fit=crop&crop=center'; // Vision/awareness
  }
  // Ethics and society
  else if (headingLower.includes('ethiek') || headingLower.includes('maatschappij') || headingLower.includes('ethics') || headingLower.includes('society') || headingLower.includes('privacy')) {
    return 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=400&h=250&fit=crop&crop=center'; // Collaboration
  }
  
  // Unique fallback images based on index to ensure no duplicates
  const uniqueImages = [
    'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&h=250&fit=crop&crop=center', // Learning
    'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=250&fit=crop&crop=center', // Data
    'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400&h=250&fit=crop&crop=center', // AI/Neural
    'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=400&h=250&fit=crop&crop=center', // Business
    'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=250&fit=crop&crop=center', // Coding
    'https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=250&fit=crop&crop=center', // Innovation
    'https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=400&h=250&fit=crop&crop=center', // Vision
    'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=250&fit=crop&crop=center', // Technology
    'https://images.unsplash.com/photo-1551434678-e076c223a692?w=400&h=250&fit=crop&crop=center', // Teamwork
    'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=400&h=250&fit=crop&crop=center', // Digital transformation
    'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=400&h=250&fit=crop&crop=center', // Innovation lab
  ];
  
  return uniqueImages[index % uniqueImages.length];
};

export const TopicDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [topic, setTopic] = useState<Topic | null>(null);
  const [loading, setLoading] = useState(true);
  const { getProgress } = useProgressStore();
  const { isModuleComplete, getCompletedCount } = useModuleCompletion();

  useEffect(() => {
    const fetchTopic = async () => {
      if (!slug) return;
      
      try {
        const data = await topicsApi.getBySlug(slug);
        setTopic(data);
      } catch (error) {
        console.error('Error fetching topic:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTopic();
  }, [slug]);

  const progress = topic ? getProgress(topic.id) : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!topic) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Deel niet gevonden</h1>
        <p className="text-lg text-gray-600 mb-8">
          Het trainingsdeel dat u zoekt, bestaat niet of is verplaatst.
        </p>
        <Link to="/topics">
          <button className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
            Terug naar overzicht
          </button>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Back button */}
      <Link 
        to="/topics" 
        className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Terug naar delen
      </Link>

      {/* Header */}
      <div className="bg-white border border-gray-200 rounded-lg p-8 mb-8">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-4 flex-1">
            <div className="w-16 h-16 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <BookOpen className="w-8 h-8 text-blue-600" />
            </div>
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {topic.title}
              </h1>
              {topic.theme && (
                <p className="text-gray-600 mb-3">Thema: {topic.theme.title}</p>
              )}
              {topic.summary && (
                <p className="text-gray-700 mb-4">{topic.summary}</p>
              )}
              
              {/* Stats */}
              <div className="flex items-center space-x-6 text-sm text-gray-600">
                <div className="flex items-center space-x-2">
                  <FileText className="w-4 h-4" />
                  <span>{topic.sections?.length || 0} modules</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>
                    {getCompletedCount(topic.id)} van {topic.sections?.length || 0} voltooid
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>{topic.questions?.length || 0} vragen</span>
                </div>
              </div>
              
              {/* Progress */}
              {progress && progress.read_pct > 0 && (
                <div className="mt-4">
                  <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                    <span>Uw voortgang</span>
                    <span className="font-medium">{progress.read_pct}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all"
                      style={{ width: `${progress.read_pct}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Start button */}
        <div className="mt-6 flex items-center space-x-4">
          <Link
            to={`/topics/${topic.slug}/section/0`}
            className="inline-flex items-center px-8 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium text-lg"
          >
            <PlayCircle className="w-5 h-5 mr-2" />
            {progress && progress.read_pct > 0 ? 'Verder leren' : 'Start met leren'}
          </Link>
        </div>
      </div>

      {/* Module list */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">
          Modules in dit deel
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {topic.sections && topic.sections.length > 0 ? (
            topic.sections.map((section, index) => {
              const isCompleted = isModuleComplete(topic.id, index);
              const moduleImage = getModuleImage(section.heading, index);
              
              return (
                <Link
                  key={section.id}
                  to={`/topics/${topic.slug}/section/${index}`}
                  className="block group h-full"
                >
                  <div className={`h-full bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 flex flex-col ${
                    isCompleted ? 'ring-2 ring-green-200' : ''
                  }`}>
                    {/* Image */}
                    <div className="relative h-40 overflow-hidden flex-shrink-0">
                      <img
                        src={moduleImage}
                        alt={section.heading}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      {isCompleted && (
                        <div className="absolute top-3 right-3 bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1">
                          <CheckCircle className="w-3 h-3" />
                          <span>Voltooid</span>
                        </div>
                      )}
                      <div className="absolute bottom-3 left-3 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-xs font-medium">
                        #{index + 1}
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="p-4 flex flex-col flex-grow">
                      {/* Title */}
                      <h3 className="text-sm font-semibold text-gray-900 mb-2 line-clamp-2 flex-grow">
                        {section.heading}
                      </h3>
                      
                      {/* Button */}
                      <div className="mt-auto">
                        <button className={`w-full px-3 py-2 rounded-md font-medium text-xs flex items-center justify-center group ${
                          isCompleted
                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        } transition-colors`}>
                          <span>{isCompleted ? 'Bekijk opnieuw' : 'Start module'}</span>
                          <ArrowRight className="w-3 h-3 ml-1 group-hover:translate-x-1 transition-transform" />
                        </button>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })
          ) : (
            <div className="col-span-full">
              <p className="text-gray-600 text-center py-4">Geen modules beschikbaar</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

